import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Search, AlertTriangle, Download, Building2, FilterX, Printer } from "lucide-react";
import { toast } from "sonner";
import {
  assembleCsv, downloadCsv, germanNumber, isoDate, slugForFilename,
} from "../lib/csv";
import { openPrintReport } from "../lib/print";

const ALL_VAL = "__all__";

const STATUS_LABEL = {
  all: "Alle",
  in_stock: "Auf Lager",
  low: "Kritisch",
  out: "Kein Bestand",
};

export default function Lagerbestand() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(() => {
    const s = params.get("stock");
    return s === "low" || s === "out" || s === "in_stock" ? s : "all";
  });
  // Lieferantenfilter — Startwert aus URL-Parameter `?supplier=`
  // (Deep-Link aus Lieferantenseite „Produkte anzeigen").
  const [supplierFilter, setSupplierFilter] = useState(() => params.get("supplier") || ALL_VAL);
  // Operations Polish 2026-08-30: `?location=<id>`-Deep-Link von
  // Lagerort-Kacheln. Wenn gesetzt, wird nur dieser Lagerort in der
  // Spaltenauswahl aktiv (statt „alle").
  const [preselectLocation] = useState(() => params.get("location") || null);
  // Anzeige-Toggle „0-Bestände anzeigen". Default: aus (0-Bestände
  // ausgeblendet), wie im Task-Text festgelegt.
  const [showZero, setShowZero] = useState(false);
  // Sichtbare Lagerorte — als Set von Location-IDs. `null` = „alle"
  // (Initialzustand bevor die Locations geladen sind, außerdem
  // Bedeutung „nichts filtern" nach Nutzer-Aktion „Alle auswählen").
  const [visibleLocs, setVisibleLocs] = useState(null);

  // Sync status ↔ URL (behält Dashboard-Kachel-Deep-Links).
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (status === "all") next.delete("stock"); else next.set("stock", status);
    if (supplierFilter === ALL_VAL) next.delete("supplier");
    else next.set("supplier", supplierFilter);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, supplierFilter]);

  const data = useQuery({
    queryKey: ["inv-by-product"],
    queryFn: async () => (await api.get("/inventory/by-product")).data,
  });
  const suppliersQ = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => (await api.get("/suppliers")).data,
  });

  // Stabilisiert die abgeleiteten Referenzen — ohne useMemo bekommt jeder
  // Render eine neue [] Instanz, wodurch downstream Hooks (useEffect/useMemo)
  // in Endlosschleifen laufen könnten und ESLint zurecht warnt.
  const rows = useMemo(() => data.data?.products || [], [data.data]);
  const locations = useMemo(() => data.data?.locations || [], [data.data]);
  const suppliers = useMemo(() => suppliersQ.data || [], [suppliersQ.data]);

  // Initialisiere Lagerort-Sichtbarkeit sobald Locations geladen sind.
  useEffect(() => {
    if (visibleLocs === null && locations.length > 0) {
      // Bei Deep-Link ?location=<id> nur diesen Lagerort vorwählen —
      // sonst alle sichtbar.
      if (preselectLocation && locations.some((l) => l.id === preselectLocation)) {
        setVisibleLocs(new Set([preselectLocation]));
      } else {
        setVisibleLocs(new Set(locations.map((l) => l.id)));
      }
    }
  }, [locations, visibleLocs, preselectLocation]);

  const effectiveVisibleIds = useMemo(() => {
    if (visibleLocs === null) return new Set(locations.map((l) => l.id));
    return visibleLocs;
  }, [visibleLocs, locations]);

  const visibleLocations = useMemo(
    () => locations.filter((l) => effectiveVisibleIds.has(l.id)),
    [locations, effectiveVisibleIds],
  );

  const allSelected = visibleLocations.length === locations.length && locations.length > 0;
  const noneSelected = visibleLocations.length === 0;

  const toggleLoc = (id) => {
    setVisibleLocs((prev) => {
      const base = prev === null ? new Set(locations.map((l) => l.id)) : new Set(prev);
      if (base.has(id)) base.delete(id);
      else base.add(id);
      return base;
    });
  };
  const selectAllLocs = () => setVisibleLocs(new Set(locations.map((l) => l.id)));
  const clearLocs = () => setVisibleLocs(new Set());

  // Zentrale Filter-Pipeline: identisch für Anzeige UND CSV-Export.
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const p = r.product;
      if (q) {
        const hay = `${p.name} ${p.ean || ""} ${p.internal_id || ""} ${p.brand || ""} ${p.category || ""} ${p.supplier_name || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      if (supplierFilter !== ALL_VAL && p.supplier_id !== supplierFilter) return false;
      // Bestandsstatus nutzt Summe über SICHTBARE Lagerorte, damit die
      // Filterung konsistent mit dem angezeigten „Gesamt" bleibt.
      const visibleTotal = Object.entries(r.by_location)
        .filter(([lid]) => effectiveVisibleIds.has(lid))
        .reduce((sum, [, qty]) => sum + qty, 0);
      if (!showZero && visibleTotal <= 0) return false;
      if (status === "low" && !(p.min_stock > 0 && 0 < visibleTotal && visibleTotal <= p.min_stock)) return false;
      if (status === "out" && visibleTotal > 0) return false;
      if (status === "in_stock" && visibleTotal <= 0) return false;
      return true;
    }).map((r) => {
      // Zeile für Anzeige/Export annotieren — Total wird über sichtbare
      // LO neu berechnet, damit „Gesamt"-Spalte + Farbcode zur
      // Auswahl passt.
      const visibleTotal = Object.entries(r.by_location)
        .filter(([lid]) => effectiveVisibleIds.has(lid))
        .reduce((sum, [, qty]) => sum + qty, 0);
      return {
        ...r,
        visible_total: visibleTotal,
        is_out_visible: visibleTotal <= 0,
        is_low_visible: r.product.min_stock > 0 && 0 < visibleTotal && visibleTotal <= r.product.min_stock,
      };
    });
  }, [rows, q, status, supplierFilter, effectiveVisibleIds, showZero]);

  const supplierName = useMemo(() => {
    if (supplierFilter === ALL_VAL) return null;
    return suppliers.find((s) => s.id === supplierFilter)?.name || null;
  }, [supplierFilter, suppliers]);

  const anyFilterActive =
    !!q || supplierFilter !== ALL_VAL || status !== "all" || showZero || !allSelected;

  const resetFilters = () => {
    setQ("");
    setSupplierFilter(ALL_VAL);
    setStatus("all");
    setShowZero(false);
    setVisibleLocs(new Set(locations.map((l) => l.id)));
  };

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Keine Zeilen im aktuellen Filter — nichts zu exportieren.");
      return;
    }
    // Spalten: Kern-Stammdaten + je sichtbarer Lagerort eine Spalte +
    // Gesamt. Beim Import in Excel/OpenOffice ergibt das genau die
    // Ansicht, die der Benutzer im Browser sieht. CSV ist reine
    // Datentabelle — Filter-Metadaten wandern in den Drucken-Export.
    const headers = [
      "Produkt",
      "SKU",
      "EAN",
      "Marke",
      "Kategorie",
      "Lieferant",
      "MwSt (%)",
      "EK",
      "VK",
      "Ab 18",
      "Mindestbestand",
      ...visibleLocations.map((l) => `LO: ${l.name}`),
      "Gesamt",
    ];
    const rowsOut = filtered.map((r) => {
      const p = r.product;
      const perLoc = visibleLocations.map((l) => r.by_location[l.id] || 0);
      return [
        p.name,
        p.internal_id || "",
        p.ean || "",
        p.brand || "",
        p.category || "",
        p.supplier_name || "",
        Number(p.vat_rate || 0),
        germanNumber(p.purchase_price),
        germanNumber(p.sales_price),
        p.age_restricted ? "Ja" : "Nein",
        p.min_stock,
        ...perLoc,
        r.visible_total,
      ];
    });
    const csv = assembleCsv({ headers, rows: rowsOut });
    const suffix = supplierName ? `-${slugForFilename(supplierName)}` : "";
    downloadCsv(`loop-bestand${suffix}-${isoDate()}.csv`, csv);
    toast.success(`${rowsOut.length} Zeilen exportiert`);
  };

  const printReport = () => {
    if (!filtered.length) {
      toast.error("Keine Zeilen im aktuellen Filter — nichts zu drucken.");
      return;
    }
    const locLabel = allSelected
      ? "Alle"
      : noneSelected
        ? "—"
        : visibleLocations.map((l) => l.name).join(", ");
    const filters = [
      { label: "Lieferant", value: supplierName || "Alle" },
      { label: "Lagerorte", value: locLabel },
      { label: "0-Bestände", value: showZero ? "eingeblendet" : "ausgeblendet" },
      { label: "Bestandsstatus", value: STATUS_LABEL[status] || "Alle" },
      { label: "Suche", value: q ? q : "—" },
    ];
    const columns = [
      { label: "Produkt" },
      { label: "SKU" },
      { label: "EAN" },
      { label: "Kategorie" },
      { label: "Lieferant" },
      { label: "MwSt.", align: "right" },
      { label: "Ab 18", align: "center" },
      ...visibleLocations.map((l) => ({ label: l.name, align: "right" })),
      { label: "Gesamt", align: "right" },
      { label: "Min.", align: "right" },
    ];
    const dataRows = filtered.map((r) => {
      const p = r.product;
      const perLoc = visibleLocations.map((l) => String(r.by_location[l.id] || 0));
      return [
        p.name,
        p.internal_id || "",
        p.ean || "",
        p.category || "",
        p.supplier_name || "",
        `${Number(p.vat_rate || 0)} %`,
        p.age_restricted ? "Ja" : "—",
        ...perLoc,
        String(r.visible_total),
        String(p.min_stock),
      ];
    });
    const result = openPrintReport({
      area: "Lagerbestand", filters, columns, rows: dataRows, resultCount: filtered.length,
    });
    if (!result.ok) {
      toast.error("Popup-Blocker verhindert die Druckvorschau. Bitte in den Browsereinstellungen erlauben.");
    }
  };

  const singleLoc = visibleLocations.length === 1;

  return (
    <div data-testid="inventory-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-cyan-400">Bestand</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">Lagerbestand</h1>
        {supplierName && (
          <div className="mt-2 text-xs uppercase tracking-widest text-cyan-400/80">
            Gefiltert nach Lieferant: <span className="text-cyan-300">{supplierName}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
        <div className="relative md:col-span-2 xl:col-span-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="inventory-search"
            placeholder="Suche: Name, EAN, Lieferant…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11 bg-zinc-900 border-zinc-800 rounded-sm"
          />
        </div>
        <Select value={supplierFilter} onValueChange={setSupplierFilter}>
          <SelectTrigger data-testid="inventory-filter-supplier" className="h-11 bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectValue placeholder="Lieferant" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value={ALL_VAL}>Alle Lieferanten</SelectItem>
            {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Lagerort-Multiselect via Popover + Checkbox-Liste */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              data-testid="inventory-filter-locations-btn"
              className="h-11 bg-zinc-900 border-zinc-800 rounded-sm justify-start font-normal"
            >
              <Building2 size={14} className="mr-2 text-cyan-400" />
              <span className="truncate text-sm">
                {noneSelected
                  ? "Keine Lagerorte"
                  : allSelected
                    ? "Alle Lagerorte"
                    : `${visibleLocations.length} von ${locations.length} Lagerorten`}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            data-testid="inventory-locations-popover"
            align="start"
            className="w-72 bg-zinc-950 border-zinc-800 p-0"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500">Lagerorte</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="inventory-locations-select-all"
                  onClick={selectAllLocs}
                  className="h-7 px-2 text-[11px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300"
                >
                  Alle
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="inventory-locations-clear"
                  onClick={clearLocs}
                  className="h-7 px-2 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-zinc-200"
                >
                  Keiner
                </Button>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {locations.map((l) => {
                const checked = effectiveVisibleIds.has(l.id);
                return (
                  <label
                    key={l.id}
                    data-testid={`inventory-loc-toggle-${l.id}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-900 cursor-pointer"
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggleLoc(l.id)} />
                    <span className="text-sm truncate flex-1">{l.name}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{l.code}</span>
                  </label>
                );
              })}
              {!locations.length && (
                <div className="px-3 py-6 text-center text-zinc-500 text-sm">Keine Lagerorte</div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="inventory-filter-status" className="h-11 bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all">Alle Bestände</SelectItem>
            <SelectItem value="in_stock">Auf Lager</SelectItem>
            <SelectItem value="low">Kritisch</SelectItem>
            <SelectItem value="out">Kein Bestand</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label
          className="inline-flex items-center gap-2 cursor-pointer select-none"
          data-testid="inventory-toggle-zero-label"
        >
          <Checkbox
            data-testid="inventory-toggle-zero"
            checked={showZero}
            onCheckedChange={(v) => setShowZero(!!v)}
          />
          <span className="text-xs uppercase tracking-widest text-zinc-300">0-Bestände anzeigen</span>
        </label>
        <Button
          data-testid="inventory-csv-export"
          onClick={exportCsv}
          variant="outline"
          size="sm"
          className="h-9 border-zinc-700 hover:bg-zinc-800 rounded-sm text-cyan-400 hover:text-cyan-300 uppercase tracking-widest text-[11px]"
        >
          <Download size={14} className="mr-2" /> CSV exportieren
        </Button>
        <Button
          data-testid="inventory-print"
          onClick={printReport}
          variant="outline"
          size="sm"
          className="h-9 border-zinc-700 hover:bg-zinc-800 rounded-sm text-zinc-200 hover:text-white uppercase tracking-widest text-[11px]"
        >
          <Printer size={14} className="mr-2" /> Drucken
        </Button>
        {anyFilterActive && (
          <Button
            data-testid="inventory-filter-reset"
            onClick={resetFilters}
            variant="ghost"
            size="sm"
            className="h-9 text-zinc-400 hover:text-zinc-200 uppercase tracking-widest text-[11px]"
          >
            <FilterX size={14} className="mr-1" /> Filter zurücksetzen
          </Button>
        )}
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 ml-auto"
          data-testid="inventory-result-count">
          {filtered.length} Zeilen
        </span>
      </div>

      <div className="border border-zinc-800 bg-zinc-900 rounded-sm w-full max-w-full overflow-x-auto">
        <table className={`w-full text-sm ${singleLoc ? "" : "min-w-max"}`}>
          <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr>
              <th className={`text-left px-3 py-3 ${singleLoc ? "" : "sticky left-0 z-10 bg-zinc-900 border-r border-zinc-800 min-w-[160px]"}`}>Produkt</th>
              <th className="text-left px-3 py-3 whitespace-nowrap">Lieferant</th>
              <th className="text-right px-3 py-3 font-mono whitespace-nowrap">MwSt.</th>
              {visibleLocations.map((l) => (
                <th key={l.id} className="text-center px-3 py-3 font-mono whitespace-nowrap">{l.name}</th>
              ))}
              <th className="text-center px-3 py-3 font-mono">Gesamt</th>
              <th className="text-center px-3 py-3 font-mono text-yellow-400">Kritisch</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800" data-testid="inventory-table-body">
            {filtered.map((r, idx) => {
              const rowTone = r.is_out_visible
                ? "bg-rose-500/10 hover:bg-rose-500/20"
                : r.is_low_visible
                  ? "bg-yellow-500/10 hover:bg-yellow-500/20"
                  : (idx % 2 === 0 ? "bg-zinc-900 hover:bg-zinc-800/60" : "bg-zinc-900/60 hover:bg-zinc-800/60");
              const totalTone = r.is_out_visible ? "text-rose-400" : r.is_low_visible ? "text-yellow-400" : "";
              return (
              <tr key={r.product.id} className={rowTone} data-testid={`inv-row-${r.product.id}`}>
                <td className={`px-3 py-3 ${singleLoc ? "" : "sticky left-0 z-10 border-r border-zinc-800 min-w-[160px] max-w-[220px] " + (r.is_out_visible ? "bg-rose-950" : r.is_low_visible ? "bg-yellow-950/60" : (idx % 2 === 0 ? "bg-zinc-900" : "bg-zinc-950"))}`}>
                  <div className="truncate">
                    {r.product.name}
                    {r.product.age_restricted && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-rose-500/20 text-rose-300 text-[10px] uppercase font-mono tracking-widest align-middle">
                        Ab 18
                      </span>
                    )}
                  </div>
                  {r.product.ean && (
                    <div className="text-xs text-zinc-500 font-mono truncate">{r.product.ean}</div>
                  )}
                </td>
                <td className="px-3 py-3 text-zinc-300 whitespace-nowrap">{r.product.supplier_name || "—"}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums text-zinc-300 whitespace-nowrap">
                  {Number(r.product.vat_rate || 0)} %
                </td>
                {visibleLocations.map((l) => {
                  const qty = r.by_location[l.id] || 0;
                  return (
                    <td key={l.id} className="text-center px-3 py-3 font-mono tabular-nums text-zinc-200 whitespace-nowrap">
                      {qty}
                    </td>
                  );
                })}
                <td className={`text-center px-3 py-3 font-mono tabular-nums font-semibold whitespace-nowrap ${totalTone}`} data-testid={`inv-total-${r.product.id}`}>{r.visible_total}</td>
                <td className="text-center px-3 py-3 font-mono tabular-nums text-zinc-500 whitespace-nowrap">{r.product.min_stock}</td>
                <td className="px-2 py-3 whitespace-nowrap text-center" data-testid={`inv-status-${r.product.id}`}>
                  {r.is_out_visible ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-rose-500/20 text-rose-300 text-[11px] uppercase font-mono tracking-widest">Leer</span>
                  ) : r.is_low_visible ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-yellow-500/20 text-yellow-300 text-[11px] uppercase font-mono tracking-widest">
                      <AlertTriangle size={12} /> Kritisch
                    </span>
                  ) : null}
                </td>
              </tr>
            );})}
            {!filtered.length && (
              <tr><td colSpan={visibleLocations.length + 6} className="px-4 py-8 text-center text-zinc-500">Keine Bestände gefunden</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
